/**
 * Users Controller (Hono)
 *
 * Custom handlers that complement the auto-mounted CRUD entity routes:
 * - GDPR endpoints (/me, /me/export, /me/consent, DELETE /me)
 * - Multi-role management (/:userId/roles)
 * - Self-delete guard (DELETE /:id blocks deleting yourself)
 */

import type { Context } from 'hono'
import { setCookie } from 'hono/cookie'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv } from '../../http/types.js'
import type { UsersService } from './users.types.js'

type C = Context<NexusEnv>

export function createUsersController(ctx: ModuleContext) {
  const usersService = ctx.services.get<UsersService>('users')

  function getClientIp(c: C): string | undefined {
    return c.req.header('x-forwarded-for')?.split(',')[0]?.trim()
      ?? c.req.header('x-real-ip')
      ?? undefined
  }

  return {
    // --- GET /me ---
    async getMe(c: C): Promise<Response> {
      const userId = c.get('user')?.id
      if (!userId) throw new ctx.core.errors.UnauthorizedError('No authenticated')
      const user = await usersService.findById(userId)
      return c.json(user)
    },

    // --- GET /me/export (GDPR Art. 15) ---
    async exportMe(c: C): Promise<Response> {
      const userId = c.get('user')?.id
      if (!userId) throw new ctx.core.errors.UnauthorizedError('No authenticated')
      const data = await usersService.exportUserData(userId)

      const date = new Date().toISOString().split('T')[0]
      c.header('Content-Type', 'application/json')
      c.header('Content-Disposition', `attachment; filename="user-data-export-${date}.json"`)
      return c.json(data)
    },

    // --- DELETE /me (GDPR Art. 17) ---
    async deleteMe(c: C): Promise<Response> {
      const userId = c.get('user')?.id
      if (!userId) throw new ctx.core.errors.UnauthorizedError('No authenticated')
      const result = await usersService.deleteAccount(userId)

      // Clear refresh token cookie
      setCookie(c, 'refreshToken', '', { path: '/api/v1', maxAge: 0 })
      return c.json(result)
    },

    // --- POST /me/consent ---
    async recordConsent(c: C): Promise<Response> {
      const userId = c.get('user')?.id
      if (!userId) throw new ctx.core.errors.UnauthorizedError('No authenticated')

      const body = (await c.req.json()) as { version: string; marketingOptIn: boolean }
      const result = await usersService.recordConsent(userId, body.version, body.marketingOptIn, {
        ipAddress: getClientIp(c),
        userAgent: c.req.header('user-agent')
      })
      return c.json(result)
    },

    // --- GET /:userId/roles ---
    async getUserRoles(c: C): Promise<Response> {
      const userId = c.req.param('userId')!
      const roles = await usersService.getUserRoles(userId)
      return c.json(roles)
    },

    // --- POST /:userId/roles ---
    async assignRole(c: C): Promise<Response> {
      const userId = c.req.param('userId')!
      const { roleId } = (await c.req.json()) as { roleId: string }
      await usersService.assignRole(userId, roleId)
      ctx.events.notify('audit.log', {
        source: 'core:users',
        action: 'role_assigned',
        actorId: c.get('user')?.id,
        resourceType: 'user',
        resourceId: userId,
        ip: getClientIp(c),
        userAgent: c.req.header('user-agent'),
        metadata: { roleId }
      })
      const roles = await usersService.getUserRoles(userId)
      return c.json(roles)
    },

    // --- PUT /:userId/roles ---
    async setRoles(c: C): Promise<Response> {
      const userId = c.req.param('userId')!
      const { roleIds } = (await c.req.json()) as { roleIds: string[] }
      await usersService.setRoles(userId, roleIds)
      ctx.events.notify('audit.log', {
        source: 'core:users',
        action: 'roles_replaced',
        actorId: c.get('user')?.id,
        resourceType: 'user',
        resourceId: userId,
        ip: getClientIp(c),
        userAgent: c.req.header('user-agent'),
        metadata: { roleIds }
      })
      const roles = await usersService.getUserRoles(userId)
      return c.json(roles)
    },

    // --- DELETE /:userId/roles/:roleId ---
    async removeRole(c: C): Promise<Response> {
      const userId = c.req.param('userId')!
      const roleId = c.req.param('roleId')!
      await usersService.removeRole(userId, roleId)
      ctx.events.notify('audit.log', {
        source: 'core:users',
        action: 'role_removed',
        actorId: c.get('user')?.id,
        resourceType: 'user',
        resourceId: userId,
        ip: getClientIp(c),
        userAgent: c.req.header('user-agent'),
        metadata: { roleId }
      })
      return c.json({ success: true })
    },

    // --- DELETE /:id --- (self-delete already blocked by selfDeleteGuard middleware in routes)
    async deleteUser(c: C): Promise<Response> {
      const id = c.req.param('id')!
      await usersService.delete(id)
      return c.body(null, 204)
    }
  }
}
